import { SeverityLevel } from '@microsoft/applicationinsights-web'

import { getAppInsights } from '@/core/providers/ConfigAppInsightsContainer/AppInsights'

/**
 * Reporte de errores del navegador a Application Insights.
 *
 * El SDK del navegador solo captura lo que NO se maneja: `window.onerror` y las promesas
 * rechazadas sin `catch`. Un error que el código atrapa y manda a la consola no sale del navegador
 * del usuario, así que en el portal no existe. De ahí que en producción hubiera una sola excepción
 * registrada en 24 h contra 233 vistas de página: no es que no falle nada, es que lo que falla se
 * queda en la consola de quien lo sufre.
 *
 * `reportarError` hace las dos cosas: deja el `console.error` de siempre —que le sirve a soporte
 * cuando está mirando la pantalla del usuario— y además manda la excepción a Application Insights
 * con el contexto, para que se pueda consultar después.
 */

/** Convierte cualquier cosa lanzada en un Error, que es lo único que el SDK sabe apilar. */
const comoError = (valor: unknown): Error => {
  if (valor instanceof Error) {
    return valor
  }

  if (typeof valor === 'string') {
    return new Error(valor)
  }

  try {
    return new Error(JSON.stringify(valor))
  } catch {
    // Referencias circulares: JSON.stringify lanza y perderíamos el reporte entero por el detalle
    return new Error(String(valor))
  }
}

/**
 * Registra un error manejado.
 *
 * @param error lo que llegó al `catch`; no hace falta que sea un `Error`.
 * @param contexto qué se estaba haciendo, en palabras. Es lo que se lee en el portal, así que
 *   conviene que diga la operación y no solo "error" (p. ej. `'Descargar proforma de la orden'`).
 * @param datos claves extra para filtrar en el portal (ids, códigos). Van a `customDimensions`.
 */
export const reportarError = (error: unknown, contexto: string, datos?: Record<string, unknown>): void => {
  console.error(`${contexto}:`, error)

  getAppInsights()?.trackException({
    exception: comoError(error),
    severityLevel: SeverityLevel.Error,
    properties: { contexto, ...datos },
  })
}

/**
 * `customDimensions` solo guarda texto plano. Un Error pasado como dato quedaría en
 * "[object Object]", que es justo la parte que hacía falta leer.
 */
const comoTexto = (valor: unknown): unknown => {
  if (valor instanceof Error) {
    return `${valor.name}: ${valor.message}`
  }

  return typeof valor === 'object' && valor !== null ? JSON.stringify(valor) : valor
}

const normalizar = (datos?: Record<string, unknown>): Record<string, unknown> | undefined =>
  datos && Object.fromEntries(Object.entries(datos).map(([clave, valor]) => [clave, comoTexto(valor)]))

/**
 * Igual que `reportarError` pero para lo que no rompe nada y aun así conviene poder contar:
 * un dato que no vino, un reintento, una respuesta fuera de orden que se descarta, una exportación
 * que salió cortada. Va como traza y no como excepción para no ensuciar el blade de Failures.
 */
export const reportarAdvertencia = (contexto: string, datos?: Record<string, unknown>): void => {
  console.warn(contexto, datos ?? '')

  getAppInsights()?.trackTrace({
    message: contexto,
    severityLevel: SeverityLevel.Warning,
    properties: normalizar(datos),
  })
}

/**
 * Telemetría de negocio: qué hizo la gente, no qué se rompió. El proyecto no tenía ni un
 * `trackEvent`, así que no había forma de responder desde el portal cuántas órdenes de pago se
 * generan por día o si alguien usa la exportación.
 *
 * Va solo en el punto donde la acción YA se confirmó, nunca al abrir el formulario, y sin datos
 * personales: ids y cantidades, no nombres de pasajeros ni correos.
 *
 * @param nombre en pasado y en español, como se va a leer en el portal: `'Órdenes de pago generadas'`.
 * @param datos cantidades e ids para poder agrupar.
 */
export const registrarEvento = (nombre: string, datos?: Record<string, unknown>): void => {
  getAppInsights()?.trackEvent({ name: nombre }, normalizar(datos))
}
